// ABOUTME: The one list of Settings pages and the fields on them. The Settings layout, the
// ⌘K search and every in-app link read their paths from here; a field's `anchor` is the id
// of the section that holds it, so `path#anchor` scrolls straight to it.

import { Building2, CreditCard, Link2, SlidersHorizontal, User, Users } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

export const SETTINGS_PATHS = {
  business: '/settings/business',
  accounts: '/settings/accounts',
  team: '/settings/team',
  plan: '/settings/plan',
  profile: '/settings/profile',
  advanced: '/settings/advanced',
} as const

export type SettingsPageKey = keyof typeof SETTINGS_PATHS

export interface SettingsPage {
  key: SettingsPageKey
  label: string
  /** One line under the label in the list. */
  summary: string
  icon: LucideIcon
}

/** The five everyday pages, in list order. Advanced is listed below them, set apart. */
export const SETTINGS_PAGES: SettingsPage[] = [
  {
    key: 'business',
    label: 'Business',
    summary: 'What Marketing knows about you',
    icon: Building2,
  },
  { key: 'accounts', label: 'Accounts', summary: 'Social, messaging and tools', icon: Link2 },
  { key: 'team', label: 'Team', summary: 'People and your AI team', icon: Users },
  { key: 'plan', label: 'Plan & credits', summary: 'Plan, balance and invoices', icon: CreditCard },
  { key: 'profile', label: 'Profile', summary: 'Your name, alerts and privacy', icon: User },
]

export const ADVANCED_PAGE: SettingsPage = {
  key: 'advanced',
  label: 'Advanced',
  summary: 'API keys, agent platforms and developer tools',
  icon: SlidersHorizontal,
}

/** `path#anchor` of one section on a Settings page. */
export function settingsHref(page: SettingsPageKey, anchor?: string): string {
  return anchor ? `${SETTINGS_PATHS[page]}#${anchor}` : SETTINGS_PATHS[page]
}

export interface SettingsField {
  label: string
  page: SettingsPageKey
  anchor: string
  /** Other words an owner might type for it. */
  keywords: string[]
}

export const SETTINGS_FIELDS: SettingsField[] = [
  {
    label: 'Business details',
    page: 'business',
    anchor: 'details',
    keywords: ['name', 'website', 'phone', 'address', 'industry', 'description', 'logo'],
  },
  { label: 'Timezone', page: 'business', anchor: 'details', keywords: ['time zone'] },
  {
    label: 'About your business',
    page: 'business',
    anchor: 'about',
    keywords: ['mission', 'pitch', 'target market', 'service area', 'price', 'certifications'],
  },
  { label: 'Services', page: 'business', anchor: 'services', keywords: ['products', 'offers'] },
  {
    label: 'Hours and FAQs',
    page: 'business',
    anchor: 'hours',
    keywords: ['opening hours', 'faq', 'questions', 'knowledge base'],
  },
  {
    label: 'Content themes',
    page: 'business',
    anchor: 'themes',
    keywords: ['keywords', 'hashtags', 'pillars'],
  },
  { label: 'Brand voice', page: 'business', anchor: 'brand-voice', keywords: ['tone', 'style'] },
  { label: 'Brand kit', page: 'business', anchor: 'brand-kit', keywords: ['colours', 'fonts'] },
  { label: 'Brand assets', page: 'business', anchor: 'assets', keywords: ['photos', 'files'] },
  {
    label: 'Your businesses',
    page: 'business',
    anchor: 'businesses',
    keywords: ['switch business', 'new business', 'delete business'],
  },
  {
    label: 'Social accounts',
    page: 'accounts',
    anchor: 'social',
    keywords: ['facebook', 'instagram', 'tiktok', 'youtube', 'linkedin', 'x', 'connect'],
  },
  {
    label: 'Messaging and Google',
    page: 'accounts',
    anchor: 'channels',
    keywords: ['gmail', 'google business profile', 'calendar', 'drive', 'whatsapp', 'slack'],
  },
  {
    label: 'Add an account by link',
    page: 'accounts',
    anchor: 'add-by-link',
    keywords: ["can't connect", 'handle'],
  },
  { label: 'Your own browser', page: 'accounts', anchor: 'own-browser', keywords: ['browser'] },
  {
    label: 'More tools',
    page: 'accounts',
    anchor: 'more-tools',
    keywords: ['quickbooks', 'jobber', 'shopify', 'canva', 'stripe', 'ads'],
  },
  { label: 'People', page: 'team', anchor: 'people', keywords: ['invite', 'teammates'] },
  {
    label: 'Marketing',
    page: 'team',
    anchor: 'marketing',
    keywords: ['autonomy', 'on its own', 'voice'],
  },
  { label: 'Image producer', page: 'team', anchor: 'image', keywords: ['ai images', 'look'] },
  { label: 'Video producer', page: 'team', anchor: 'video', keywords: ['own voice', 'clone'] },
  {
    label: 'Outreach',
    page: 'team',
    anchor: 'outreach',
    keywords: ['mailboxes', 'sender', 'cold email'],
  },
  { label: 'Plan', page: 'plan', anchor: 'plan', keywords: ['subscription', 'upgrade', 'billing'] },
  {
    label: 'Credits and usage',
    page: 'plan',
    anchor: 'credits',
    keywords: ['balance', 'top up', 'usage'],
  },
  { label: 'Invoices', page: 'plan', anchor: 'invoices', keywords: ['payments', 'history'] },
  { label: 'Setup help', page: 'plan', anchor: 'setup', keywords: ['done for you'] },
  { label: 'Refer a friend', page: 'plan', anchor: 'referral', keywords: ['referral'] },
  { label: 'Profile', page: 'profile', anchor: 'profile', keywords: ['name', 'phone', 'avatar'] },
  {
    label: 'Notifications',
    page: 'profile',
    anchor: 'notifications',
    keywords: ['alerts', 'emails'],
  },
  {
    label: 'Password and sign-in',
    page: 'profile',
    anchor: 'security',
    keywords: ['password', 'two-factor', 'mfa'],
  },
  {
    label: 'Privacy',
    page: 'profile',
    anchor: 'privacy',
    keywords: ['export', 'delete account', 'consent'],
  },
  {
    label: 'API keys',
    page: 'advanced',
    anchor: 'mcp',
    keywords: ['mcp', 'claude', 'chatgpt', 'cursor'],
  },
  { label: 'Agent platforms', page: 'advanced', anchor: 'agent-platforms', keywords: ['oauth'] },
  { label: 'Credentials', page: 'advanced', anchor: 'credentials', keywords: ['secrets'] },
  {
    label: 'Custom connectors',
    page: 'advanced',
    anchor: 'custom-connectors',
    keywords: ['mcp server'],
  },
  { label: 'Email and SMS sending', page: 'advanced', anchor: 'sending', keywords: ['klaviyo'] },
  { label: 'Website events', page: 'advanced', anchor: 'website-events', keywords: ['signups'] },
  { label: 'SSH access', page: 'advanced', anchor: 'ssh', keywords: ['server'] },
  { label: 'Tailscale', page: 'advanced', anchor: 'tailscale', keywords: ['tailnet'] },
]
